import { Link, Navigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  ShieldCheck, LogOut, RefreshCw, Search, Phone, Mail, Download, Loader2,
  Eye, Printer, Share2, MessageCircle, MapPin, Clock, Activity, Upload,
} from "lucide-react";
import { BRAND } from "@/lib/brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "sw_admin_token";

const STATUS = {
  pending:            { label: "Pending",           color: "bg-amber-500/15 text-amber-300 border-amber-400/40" },
  attended:           { label: "Attended",          color: "bg-emerald-500/15 text-emerald-300 border-emerald-400/40" },
  call_not_received:  { label: "Call not received", color: "bg-rose-500/15 text-rose-300 border-rose-400/40" },
  in_progress:        { label: "In progress",       color: "bg-brand-cyan/15 text-brand-cyan border-brand-cyan/40" },
  converted:          { label: "Converted",         color: "bg-violet-500/15 text-violet-300 border-violet-400/40" },
  not_interested:     { label: "Not interested",    color: "bg-white/10 text-white/60 border-white/20" },
};
const STATUS_KEYS = Object.keys(STATUS);

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [pw, setPw] = useState("");
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [preview, setPreview] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [visits, setVisits] = useState({ stats: { total: 0, today: 0, this_week: 0, unique_today: 0 }, recent: [] });
  const lastVisitIdRef = useRef(null);

  const fetchVisits = async (tk = token) => {
    if (!tk) return;
    try {
      const { data } = await axios.get(`${API}/visits?limit=30`, { headers: { "X-Admin-Token": tk } });
      const newest = data?.recent?.[0]?.id;
      // Toast when a truly new visit arrives (skip on first load)
      if (lastVisitIdRef.current && newest && newest !== lastVisitIdRef.current) {
        const v = data.recent[0];
        toast("👁 New visitor", { description: `${v.path} · ${v.referrer_source || "Direct"} · ${v.device || "Other"}` });
      }
      lastVisitIdRef.current = newest || lastVisitIdRef.current;
      setVisits(data);
    } catch (err) { /* silent */ }
  };

  const fetchLeads = async (tk = token) => {
    if (!tk) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/leads`, { headers: { "X-Admin-Token": tk } });
      setLeads(data || []);
      setAuthed(true);
      sessionStorage.setItem(TOKEN_KEY, tk);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Incorrect admin password.");
        setAuthed(false);
        sessionStorage.removeItem(TOKEN_KEY);
        setToken("");
      } else {
        toast.error("Failed to load leads.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (token) { fetchLeads(token); fetchVisits(token); } /* eslint-disable-next-line */ }, []);

  // Poll for new visitors every 15 seconds while admin is logged in
  useEffect(() => {
    if (!authed) return;
    const iv = setInterval(() => fetchVisits(), 15000);
    return () => clearInterval(iv);
    // eslint-disable-next-line
  }, [authed, token]);

  const login = (e) => {
    e.preventDefault();
    if (!pw.trim()) return;
    const tk = pw.trim();
    setToken(tk);
    fetchLeads(tk);
    fetchVisits(tk);
  };

  const logout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken(""); setAuthed(false); setLeads([]); setPw("");
  };

  const updateStatus = async (id, status) => {
    setSavingId(id);
    try {
      const { data } = await axios.patch(
        `${API}/leads/${id}`,
        { status },
        { headers: { "X-Admin-Token": token } }
      );
      setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status: data.status } : l)));
      toast.success(`Marked as "${STATUS[status].label}"`);
    } catch (err) {
      toast.error("Could not update status.");
    } finally {
      setSavingId(null);
    }
  };

  const exportCsv = () => {
    if (!leads.length) return;
    const cols = ["created_at", "name", "phone", "email", "property_type", "cameras", "status", "message", "source"];
    const csv = [
      cols.join(","),
      ...leads.map((l) => cols.map((c) => `"${(l[c] ?? "").toString().replace(/"/g, '""')}"`).join(",")),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `securewatch-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const backupData = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/export`, { headers: { "X-Admin-Token": token } });
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `securewatch-backup-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      const c = data.counts || {};
      toast.success(`Backup downloaded (${Object.values(c).reduce((a, b) => a + b, 0)} rows)`);
    } catch { toast.error("Backup failed"); }
  };

  const restoreData = async (file) => {
    if (!file) return;
    if (!window.confirm(`Restore data from ${file.name}?\n\nThis will upsert (add or update by id) all rows from the backup into this environment. Existing rows not in the backup are kept.`)) return;
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      const collections = parsed.collections || parsed;
      const res = await axios.post(`${API}/admin/import`, { collections, mode: "upsert" }, { headers: { "X-Admin-Token": token } });
      const r = res.data?.results || {};
      const summary = Object.entries(r).map(([k, v]) => `${k}: +${v.inserted}/~${v.updated}`).join(" · ");
      toast.success(`Restore complete — ${summary}`);
      fetchLeads(); fetchVisits();
    } catch (e) {
      toast.error("Restore failed — is this a valid backup file?");
    }
  };

  const leadSummary = (l) =>
    `SECURE WATCH — Lead\n\n` +
    `Name: ${l.name}\nPhone: ${l.phone}\n` +
    (l.email ? `Email: ${l.email}\n` : "") +
    (l.property_type ? `Property: ${l.property_type}\n` : "") +
    (l.cameras ? `Cameras: ${l.cameras}\n` : "") +
    (l.message ? `Message: ${l.message}\n` : "") +
    `Status: ${STATUS[l.status || "pending"].label}\nWhen: ${formatDate(l.created_at)}\n`;

  const shareLead = async (l) => {
    const text = leadSummary(l);
    if (navigator.share) {
      try {
        await navigator.share({ title: `Lead — ${l.name}`, text });
      } catch {/* user cancelled */}
    } else {
      try {
        await navigator.clipboard.writeText(text);
        toast.success("Lead copied to clipboard");
      } catch {
        toast.error("Sharing not supported on this device.");
      }
    }
  };

  const whatsappLead = (l) => {
    const num = (l.phone || "").replace(/\D/g, "").slice(-10);
    if (!num) return toast.error("Invalid phone number.");
    const msg = encodeURIComponent(
      `Hi ${l.name.split(" ")[0]}, this is Secure Watch. Thanks for your enquiry — can we schedule a free security audit at your convenience?`
    );
    window.open(`https://wa.me/91${num}?text=${msg}`, "_blank", "noopener");
  };

  const printLead = (l) => {
    const html = `
      <!doctype html><html><head><meta charset="utf-8"/><title>Lead — ${escapeHtml(l.name)}</title>
      <style>
        *{box-sizing:border-box;font-family:-apple-system,BlinkMacSystemFont,Segoe UI,Arial,sans-serif}
        body{margin:32px;color:#111}
        h1{font-size:20px;margin:0 0 4px}
        .muted{color:#666;font-size:12px}
        .brand{display:flex;align-items:center;gap:12px;margin-bottom:16px;border-bottom:2px solid #111;padding-bottom:12px}
        .brand img{height:44px}
        table{border-collapse:collapse;width:100%;margin-top:16px}
        th,td{border:1px solid #ddd;padding:10px 12px;text-align:left;font-size:14px}
        th{background:#f6f6f6;width:180px}
        .status{display:inline-block;padding:4px 10px;border-radius:99px;background:#eef;color:#334;font-size:12px;font-weight:600}
        .foot{margin-top:24px;font-size:11px;color:#888}
      </style></head><body>
      <div class="brand">
        <img src="${BRAND.logoUrl}" alt="Secure Watch"/>
        <div><h1>SECURE WATCH · Lead Details</h1>
          <div class="muted">Guwahati · A Smart Security Solution</div></div>
      </div>
      <table>
        <tr><th>Name</th><td>${escapeHtml(l.name)}</td></tr>
        <tr><th>Phone</th><td>${escapeHtml(l.phone)}</td></tr>
        <tr><th>Email</th><td>${escapeHtml(l.email || "—")}</td></tr>
        <tr><th>Property type</th><td>${escapeHtml(l.property_type || "—")}</td></tr>
        <tr><th>Cameras</th><td>${escapeHtml(l.cameras || "—")}</td></tr>
        <tr><th>Message</th><td style="white-space:pre-wrap">${escapeHtml(l.message || "—")}</td></tr>
        <tr><th>Status</th><td><span class="status">${escapeHtml(STATUS[l.status || "pending"].label)}</span></td></tr>
        <tr><th>Received</th><td>${escapeHtml(formatDate(l.created_at))}</td></tr>
        <tr><th>Source</th><td>${escapeHtml(l.source || "website")}</td></tr>
      </table>
      <p class="foot">Printed from Secure Watch Admin · ${new Date().toLocaleString("en-IN")}</p>
      <script>window.onload=()=>{window.print();setTimeout(()=>window.close(),300);}</script>
      </body></html>`;
    const w = window.open("", "_blank", "width=760,height=900");
    if (!w) return toast.error("Popup blocked — allow popups to print.");
    w.document.write(html); w.document.close();
  };

  const filtered = leads.filter((l) => {
    if (filterStatus !== "all" && (l.status || "pending") !== filterStatus) return false;
    if (!q) return true;
    const s = q.toLowerCase();
    return [l.name, l.phone, l.email, l.property_type, l.cameras, l.message, l.ticket_number]
      .some((v) => (v || "").toLowerCase().includes(s));
  });

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center px-5 bg-[#0A0A0B]">
        <form onSubmit={login} data-testid="admin-login-form" className="w-full max-w-sm rounded-2xl border border-white/10 bg-[#0F0F13] p-8">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-brand-cyan/15 border border-brand-cyan/40 text-brand-cyan flex items-center justify-center">
              <ShieldCheck className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-heading text-xl font-extrabold text-white">Secure Watch · Admin</h1>
              <p className="text-xs text-white/50">Sign in to view leads</p>
            </div>
          </div>
          <div className="mt-6">
            <Label className="text-white/70 text-xs">Admin password</Label>
            <Input type="password" data-testid="admin-password-input" value={pw} onChange={(e) => setPw(e.target.value)} autoFocus
              className="mt-1 bg-black/40 border-white/10 text-white focus-visible:ring-brand-cyan" />
          </div>
          <Button type="submit" data-testid="admin-login-btn" disabled={loading} className="mt-5 w-full bg-brand-cyan text-black hover:bg-brand-cyan/90 font-bold">
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Sign in
          </Button>
          <p className="text-[11px] text-white/40 mt-4 leading-relaxed">
            Password lives in <code className="text-white/60">backend/.env</code> as <code>ADMIN_TOKEN</code>.
          </p>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0B] text-white">
      <header className="border-b border-white/10 bg-black/60 backdrop-blur sticky top-0 z-10">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-md bg-white p-1 flex items-center justify-center">
              <img src={BRAND.logoUrl} alt="Secure Watch" className="h-full w-full object-contain" />
            </div>
            <div className="leading-none">
              <div className="font-heading font-extrabold text-white text-sm">SECURE WATCH · Admin</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-brand-cyan">Leads Dashboard</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => { fetchLeads(); fetchVisits(); }} data-testid="admin-refresh-btn" className="border-white/15 text-white hover:bg-white/10">
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              <span className="hidden sm:inline ml-1">Refresh</span>
            </Button>
            <Link to="/admin/billing" data-testid="nav-billing">
              <Button variant="outline" size="sm" className="border-brand-cyan/40 text-brand-cyan hover:bg-brand-cyan/10">
                Billing →
              </Button>
            </Link>
            <Button variant="outline" size="sm" onClick={exportCsv} data-testid="admin-export-btn" className="border-white/15 text-white hover:bg-white/10" disabled={!leads.length}>
              <Download className="h-4 w-4" />
              <span className="hidden sm:inline ml-1">CSV</span>
            </Button>
            <Button variant="outline" size="sm" onClick={backupData} data-testid="admin-backup-btn" title="Download full backup (JSON) — includes clients, invoices, expenses, catalog, staff, categories, leads" className="border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10">
              <Download className="h-4 w-4" />
              <span className="hidden sm:inline ml-1">Backup</span>
            </Button>
            <label data-testid="admin-restore-label" title="Restore from backup file (upsert)" className="inline-flex items-center gap-1 h-9 px-3 rounded-md border border-amber-500/30 text-amber-300 hover:bg-amber-500/10 cursor-pointer text-sm font-medium">
              <Upload className="h-4 w-4" />
              <span className="hidden sm:inline">Restore</span>
              <input data-testid="admin-restore-input" type="file" accept="application/json" className="hidden" onChange={(e) => { restoreData(e.target.files?.[0]); e.target.value = ""; }} />
            </label>
            <Button variant="ghost" size="sm" onClick={logout} data-testid="admin-logout-btn" className="text-white/70 hover:text-white hover:bg-white/10">
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline ml-1">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 lg:px-8 py-8">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <StatCard label="Total Leads" value={leads.length} />
          <StatCard label="This week" value={leads.filter((l) => daysAgo(l.created_at) <= 7).length} />
          <StatCard label="Today" value={leads.filter((l) => daysAgo(l.created_at) < 1).length} />
          <StatCard label="Pending" value={leads.filter((l) => (l.status || "pending") === "pending").length} />
        </div>

        {/* Live Visits panel */}
        <div className="rounded-2xl border border-brand-cyan/30 bg-gradient-to-br from-brand-cyan/10 to-transparent p-5 mb-6" data-testid="visits-panel">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-brand-cyan/20 border border-brand-cyan/40 text-brand-cyan flex items-center justify-center">
                <Activity className="h-5 w-5" />
              </div>
              <div>
                <div className="font-heading font-bold text-white">Live Website Visits</div>
                <div className="text-[11px] text-white/60">Auto-refreshes every 15 seconds · you'll get a toast when a new visitor arrives</div>
              </div>
            </div>
            <div className="flex gap-4 sm:gap-6">
              <MiniStat label="Today" value={visits.stats.today} accent />
              <MiniStat label="Unique today" value={visits.stats.unique_today} />
              <MiniStat label="This week" value={visits.stats.this_week} />
              <MiniStat label="All-time" value={visits.stats.total} />
            </div>
          </div>

          {visits.recent.length > 0 && (
            <div className="mt-4 max-h-64 overflow-y-auto rounded-lg border border-white/10 bg-black/40 divide-y divide-white/5" data-testid="visits-list">
              {visits.recent.slice(0, 12).map((v) => (
                <div key={v.id} className="flex items-center gap-3 px-3 py-2 text-xs">
                  <span className="h-1.5 w-1.5 rounded-full bg-brand-cyan pulse-dot shrink-0" />
                  <span className="text-white/85 font-mono truncate min-w-[90px]">{v.path}</span>
                  <span className={`whitespace-nowrap text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded border ${refPillCx(v.referrer_source)}`}>
                    {v.referrer_source || "Direct"}
                  </span>
                  <span className="whitespace-nowrap text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded border border-white/15 bg-white/5 text-white/70">
                    {v.device || "Other"}
                  </span>
                  <span className="text-white/45 whitespace-nowrap ml-auto">{formatDate(v.created_at)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
            <Input data-testid="admin-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, phone, email, message or SWA reference…" className="pl-9 bg-[#0F0F13] border-white/10 text-white" />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger data-testid="admin-filter-status" className="w-full sm:w-56 bg-[#0F0F13] border-white/10 text-white">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent className="bg-[#0F0F13] border-white/10 text-white">
              <SelectItem value="all">All statuses</SelectItem>
              {STATUS_KEYS.map((k) => <SelectItem key={k} value={k}>{STATUS[k].label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="rounded-2xl border border-white/10 bg-[#0F0F13] overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                <TableHead className="text-white/60">When</TableHead>
                <TableHead className="text-white/60">Name</TableHead>
                <TableHead className="text-white/60">Phone</TableHead>
                <TableHead className="text-white/60 hidden md:table-cell">Email</TableHead>
                <TableHead className="text-white/60 hidden lg:table-cell">Property</TableHead>
                <TableHead className="text-white/60 hidden lg:table-cell">Cameras</TableHead>
                <TableHead className="text-white/60 w-44">Status</TableHead>
                <TableHead className="text-white/60 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody data-testid="admin-leads-table">
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-white/50 py-10">
                    {loading ? "Loading…" : "No leads match your filter."}
                  </TableCell>
                </TableRow>
              )}
              {filtered.map((l) => {
                const st = l.status || "pending";
                return (
                  <TableRow key={l.id} className="border-white/10 hover:bg-white/5" data-testid={`lead-row-${l.id}`}>
                    <TableCell className="text-white/60 text-xs whitespace-nowrap">{formatDate(l.created_at)}</TableCell>
                    <TableCell className="text-white font-semibold">
                      <div>{l.name}</div>
                      {l.ticket_number && <div className="text-[10px] font-mono text-brand-cyan mt-0.5">{l.ticket_number}</div>}
                    </TableCell>
                    <TableCell className="text-white/80 font-mono">{l.phone}</TableCell>
                    <TableCell className="text-white/70 hidden md:table-cell">{l.email || "—"}</TableCell>
                    <TableCell className="text-white/70 hidden lg:table-cell">
                      {l.property_type ? <Badge variant="secondary" className="bg-white/10 text-white capitalize">{l.property_type}</Badge> : "—"}
                    </TableCell>
                    <TableCell className="text-white/70 hidden lg:table-cell">{l.cameras || "—"}</TableCell>
                    <TableCell>
                      <Select value={st} onValueChange={(v) => updateStatus(l.id, v)}>
                        <SelectTrigger data-testid={`status-select-${l.id}`} className={`h-8 text-xs w-full ${STATUS[st].color}`}>
                          {savingId === l.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <SelectValue />}
                        </SelectTrigger>
                        <SelectContent className="bg-[#0F0F13] border-white/10 text-white">
                          {STATUS_KEYS.map((k) => <SelectItem key={k} value={k}>{STATUS[k].label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        <IconBtn title="Preview" testid={`preview-${l.id}`} onClick={() => setPreview(l)}><Eye className="h-3.5 w-3.5" /></IconBtn>
                        <IconBtn title="Print" testid={`print-${l.id}`} onClick={() => printLead(l)}><Printer className="h-3.5 w-3.5" /></IconBtn>
                        <IconBtn title="Share" testid={`share-${l.id}`} onClick={() => shareLead(l)}><Share2 className="h-3.5 w-3.5" /></IconBtn>
                        <IconBtn title="WhatsApp" testid={`wa-${l.id}`} color="text-[#25D366] border-[#25D366]/40 hover:bg-[#25D366]/15" onClick={() => whatsappLead(l)}>
                          <MessageCircle className="h-3.5 w-3.5" />
                        </IconBtn>
                        <a href={`tel:${l.phone}`} title="Call" data-testid={`call-${l.id}`} className="h-8 w-8 rounded-md bg-brand-cyan/10 border border-brand-cyan/40 text-brand-cyan flex items-center justify-center hover:bg-brand-cyan/20">
                          <Phone className="h-3.5 w-3.5" />
                        </a>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </main>

      {/* Preview dialog */}
      <Dialog open={!!preview} onOpenChange={(v) => !v && setPreview(null)}>
        <DialogContent className="bg-[#0F0F13] border-white/10 text-white sm:max-w-lg" data-testid="preview-dialog">
          {preview && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl">{preview.name}</DialogTitle>
                <DialogDescription className="text-white/50 flex flex-wrap items-center gap-2 text-xs">
                  <Clock className="h-3 w-3" /> {formatDate(preview.created_at)}
                  {preview.ticket_number && (
                    <span className="font-mono text-brand-cyan bg-brand-cyan/10 border border-brand-cyan/30 px-2 py-0.5 rounded">
                      {preview.ticket_number}
                    </span>
                  )}
                  <span className={`px-2 py-0.5 rounded-full border text-[10px] ${STATUS[preview.status || "pending"].color}`}>
                    {STATUS[preview.status || "pending"].label}
                  </span>
                </DialogDescription>
              </DialogHeader>

              <div className="grid gap-3 py-2 text-sm">
                <Field icon={<Phone className="h-4 w-4" />} label="Phone" value={<a className="text-brand-cyan hover:underline" href={`tel:${preview.phone}`}>{preview.phone}</a>} />
                {preview.email && <Field icon={<Mail className="h-4 w-4" />} label="Email" value={<a className="text-brand-cyan hover:underline break-all" href={`mailto:${preview.email}`}>{preview.email}</a>} />}
                {preview.property_type && <Field icon={<MapPin className="h-4 w-4" />} label="Property" value={<span className="capitalize">{preview.property_type}</span>} />}
                {preview.existing_cctv && <Field icon={<Eye className="h-4 w-4" />} label="Existing CCTV" value={<span className="capitalize">{preview.existing_cctv}</span>} />}
                {preview.cameras && <Field icon={<Eye className="h-4 w-4" />} label="Cameras" value={preview.cameras} />}
                {(preview.camera_brand || preview.camera_type || preview.perimeter_security) && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {preview.camera_brand && <MiniField label="Brand" value={preview.camera_brand} />}
                    {preview.camera_type && <MiniField label="Type" value={preview.camera_type} />}
                    {preview.perimeter_security && <MiniField label="Perimeter" value={preview.perimeter_security} />}
                  </div>
                )}
                {preview.services?.length > 0 && (
                  <div className="rounded-lg border border-white/10 bg-black/40 p-3">
                    <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1.5">Services interested</div>
                    <div className="flex flex-wrap gap-1.5">
                      {preview.services.map((s) => (
                        <span key={s} className="text-[11px] px-2 py-0.5 rounded-full border border-brand-cyan/40 bg-brand-cyan/10 text-brand-cyan">{s}</span>
                      ))}
                    </div>
                  </div>
                )}
                {(preview.audit_mode || preview.preferred_date || preview.preferred_time) && (
                  <div className="grid grid-cols-3 gap-2">
                    {preview.audit_mode && <MiniField label="Mode" value={preview.audit_mode} />}
                    {preview.preferred_date && <MiniField label="Date" value={preview.preferred_date} />}
                    {preview.preferred_time && <MiniField label="Time" value={preview.preferred_time} />}
                  </div>
                )}
                {preview.property_address && (
                  <div className="rounded-lg border border-white/10 bg-black/40 p-3">
                    <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">Address</div>
                    <p className="text-white/80 whitespace-pre-wrap">{preview.property_address}</p>
                  </div>
                )}
                {preview.message && (
                  <div className="rounded-lg border border-white/10 bg-black/40 p-3">
                    <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">Security concerns</div>
                    <p className="text-white/80 whitespace-pre-wrap">{preview.message}</p>
                  </div>
                )}
              </div>

              <DialogFooter className="flex-row justify-between gap-2 sm:justify-between">
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => printLead(preview)} className="border-white/20 text-white hover:bg-white/10">
                    <Printer className="h-3.5 w-3.5 mr-1" /> Print
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => shareLead(preview)} className="border-white/20 text-white hover:bg-white/10">
                    <Share2 className="h-3.5 w-3.5 mr-1" /> Share
                  </Button>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => whatsappLead(preview)} className="bg-[#25D366] hover:bg-[#25D366]/80 text-white">
                    <MessageCircle className="h-3.5 w-3.5 mr-1" /> WhatsApp
                  </Button>
                  <a href={`tel:${preview.phone}`}>
                    <Button size="sm" className="bg-brand-cyan text-black hover:bg-brand-cyan/90">
                      <Phone className="h-3.5 w-3.5 mr-1" /> Call
                    </Button>
                  </a>
                </div>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function IconBtn({ children, onClick, title, testid, color }) {
  return (
    <button
      onClick={onClick}
      title={title}
      data-testid={testid}
      className={`h-8 w-8 rounded-md border flex items-center justify-center transition-colors ${
        color || "bg-white/5 border-white/15 text-white hover:bg-white/10"
      }`}
    >
      {children}
    </button>
  );
}

function MiniField({ label, value }) {
  return (
    <div className="rounded-lg border border-white/10 bg-black/40 p-2.5">
      <div className="text-[9px] uppercase tracking-widest text-white/40">{label}</div>
      <div className="text-xs text-white/85 mt-0.5">{value}</div>
    </div>
  );
}


function Field({ icon, label, value }) {
  return (
    <div className="flex items-start gap-3 rounded-lg border border-white/10 bg-black/30 p-3">
      <div className="text-brand-cyan mt-0.5">{icon}</div>
      <div className="flex-1">
        <div className="text-[10px] uppercase tracking-widest text-white/40">{label}</div>
        <div className="text-white text-sm">{value}</div>
      </div>
    </div>
  );
}

function refPillCx(src) {
  const map = {
    Google:      "border-blue-500/40 bg-blue-500/10 text-blue-300",
    Facebook:    "border-[#1877F2]/40 bg-[#1877F2]/10 text-[#7cb0ff]",
    Instagram:   "border-pink-500/40 bg-pink-500/10 text-pink-300",
    WhatsApp:    "border-[#25D366]/40 bg-[#25D366]/10 text-[#7de3a6]",
    LinkedIn:    "border-sky-500/40 bg-sky-500/10 text-sky-300",
    "X / Twitter": "border-white/25 bg-white/10 text-white/80",
    YouTube:     "border-red-500/40 bg-red-500/10 text-red-300",
    Telegram:    "border-cyan-500/40 bg-cyan-500/10 text-cyan-300",
    Bing:        "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
    DuckDuckGo:  "border-orange-500/40 bg-orange-500/10 text-orange-300",
    Direct:      "border-brand-cyan/40 bg-brand-cyan/10 text-brand-cyan",
    Internal:    "border-white/15 bg-white/5 text-white/50",
  };
  return map[src] || "border-white/15 bg-white/5 text-white/70";
}

function MiniStat({ label, value, accent }) {
  return (
    <div className="text-center">
      <div className={`font-heading text-2xl sm:text-3xl font-extrabold leading-none ${accent ? "text-brand-cyan text-glow" : "text-white"}`}>
        {value}
      </div>
      <div className="text-[10px] uppercase tracking-widest text-white/50 mt-1">{label}</div>
    </div>
  );
}

function short(u) {
  if (!u) return "";
  try {
    const url = new URL(u);
    return url.hostname.replace(/^www\./, "");
  } catch { return u.slice(0, 32); }
}

function StatCard({ label, value }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-[#0F0F13] p-4">
      <div className="text-[11px] uppercase tracking-[0.18em] text-white/50">{label}</div>
      <div className="font-heading text-3xl font-extrabold text-white mt-1">{value}</div>
    </div>
  );
}

function daysAgo(iso) {
  if (!iso) return Infinity;
  return (Date.now() - new Date(iso).getTime()) / 86_400_000;
}

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-IN", {
    day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
  });
}

function escapeHtml(s) {
  return String(s ?? "").replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c])
  );
}
